import { useEffect, useState } from 'react'

/**
 * Persisted user-level UI preferences. The Lost Club is voice-first;
 * the only opt-out today is `textInputEnabled`, which surfaces the
 * text input + send button as a fallback when voice isn't workable.
 *
 * Cross-component sync: each `useSettings()` call has its own React state,
 * so toggling settings from the drawer wouldn't propagate to Home/Venue
 * without help. We bridge updates with a custom DOM event so every mounted
 * hook hears the change in the same render cycle.
 */

const SETTINGS_KEY = 'tlc_settings'
const SETTINGS_EVENT = 'tlc:settings-changed'

interface Settings {
  textInputEnabled: boolean
}

const DEFAULTS: Settings = {
  textInputEnabled: false,
}

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (!raw) return DEFAULTS
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) }
  } catch {
    return DEFAULTS
  }
}

function saveSettings(s: Settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s))
  } catch {
    // Private mode or quota exceeded — silently ignore.
  }
}

export function useSettings(): [Settings, (patch: Partial<Settings>) => void] {
  const [settings, setSettings] = useState<Settings>(() => loadSettings())

  // Re-read from storage whenever any other instance dispatches the bridge
  // event, OR another tab fires a native 'storage' event.
  useEffect(() => {
    const sync = () => setSettings(loadSettings())
    window.addEventListener(SETTINGS_EVENT, sync)
    window.addEventListener('storage', (e) => {
      if (e.key === SETTINGS_KEY) sync()
    })
    return () => {
      window.removeEventListener(SETTINGS_EVENT, sync)
    }
  }, [])

  const update = (patch: Partial<Settings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch }
      saveSettings(next)
      // Tell every other useSettings() instance to re-read.
      window.dispatchEvent(new CustomEvent(SETTINGS_EVENT))
      return next
    })
  }

  return [settings, update]
}
